<template>
    <div>
        <Loader v-if="loading" />
        <div v-else class="container-fluid mt-45">
            <div class="row mb-4">
                <div class="col-md-6">
                    <Breadcrumbs />
                </div>

                <div class="col-md-6 text-right mt-4 mt-md-0">
                    <button v-show="!facilitiesAllDisabled" @click="assignTestKitToPatient()" class="btn btn-success btn-lg btn-assign-test btn-block-mobile lg-shadow">Assign a Test Kit</button>
                </div>
            </div>

            <PatientDetails
                v-if="Object.keys(patientDetails).length"
                :patient="patientDetails"
                :hideView="Boolean(true)"
                class="mt-4" 
            />

            <PatientTests
                v-if="patientTests.results.length"
                :key="patientTestsComponentKey"
                :tests="patientTests"
                :patientId="$route.params.id"
                :organisationId="organisationId"
                class="mt-4"
            />

            <PatientNotes
                :notes="patientNotes"
                :patientId="$route.params.id"
                v-on:reload="getPatientNotes()"
            />
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapActions, mapGetters} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import Loader from '../../../Common/components/Loader.vue'
import PatientDetails from '../../../Common/components/PatientDetailsComponent.vue'
import PatientTests from '@/components/PatientTestsComponent.vue'
import PatientNotes from '@/components/PatientNotesComponent.vue'
import FulfilmentOptions from '../../../Common/enums/FulfilmentOptionsType'
import IssueMethod from '../../../Common/enums/IssueMethod'
export default {
    name: 'ViewPatient',

    components: {
        Breadcrumbs,
        Loader,
        PatientDetails,
        PatientTests,
        PatientNotes
    },

    data() {
        return {
            loading: true,
            isBusy: false,
            sortColumn: "TEST_NUMBER",
            sortType: "DESCENDING",
            patientDetails: {},
            patientTests: {},
            patientNotes: [],
            patientTestsComponentKey: 1,
            issueInPersonOverride: process.env.VUE_APP_ISSUE_IN_PERSON_OVERRIDE == 1 ? true : false,
            FulfilmentOptions,
            IssueMethod
        }
    },

    watch: {
        shouldRefreshPatientTestsComponent: async function (val) {
            if(val === true) {
                try {
                    await this.getPatientTests()
                    this.patientTestsComponentKey += 1
                } catch {
                    this.setNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Something went wrong while fetching the data. Please try again'
                    })
                }
                
                this.setShouldRefreshPatientTestsComponent(false)
            }
        }
    },

    async mounted() {
        try {
            await this.getPatientDetails()
            await this.getPatientTests()
            await this.getPatientNotes()
        }
        catch {
            this.setNotification({
                type: 'error',
                title: 'Error',
                message: 'Something went wrong while fetching the data. Please try again'
            })
        }
        finally {
            this.loading = false
        }
    },

    computed: {
        ...mapGetters({
            organisationId: 'UserStore/GetUserOrganisationId',
            facilitiesAllDisabled: 'FacilityStore/GetAllFacilitiesDisabled',
            shouldRefreshPatientTestsComponent: 'AppStore/GetShouldRefreshPatientTestsComponent',
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings',
        })
    },

    methods: {
        ...mapActions({
            setPatientDetailsInTestStore: 'TestStore/SetPatientDetails',
            clearTestStore: 'TestStore/ClearData',
            setNotification: 'NotificationStore/SetNotification',
            setShouldRefreshPatientTestsComponent: 'AppStore/SetShouldRefreshPatientTestsComponent',
            setShippingAddressTypeInTestStoreAction: 'TestStore/SetShippingAddressType',
            setDeliveryMethodInTestStoreAction: 'TestStore/SetDeliveryMethod'
        }),

        async getPatientDetails() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.$route.params.id}`)

            this.patientDetails = response.data
        },

        async getPatientTests() {
            this.isBusy = true

            let searchModel = {
                organisationId: this.organisationId,
                patientId: this.$route.params.id,
                pageSize: process.env.VUE_APP_PAGINATION_PAGE_SIZE,
                offset: 0,
                sortColumn: this.sortColumn,
                sortType: this.sortType,
                sortBySync: "testNumber"
            }

            try {
                let response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search`, searchModel)
                this.patientTests = response.data
            } catch {
                this.isBusy = false
                throw Exception
            } finally {
                this.isBusy = false
            }
            
        },

        async getPatientNotes() {
            let response = await axios.get(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patientnotes/${this.organisationId}/${this.$route.params.id}/100/0`)

            this.patientNotes = response.data.results
        },

        async assignTestKitToPatient() {
            this.clearTestStore()

            this.setPatientDetailsInTestStore(this.patientDetails)

            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.POST) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.POST)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }

            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.IN_PERSON) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.IN_PERSON)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }
            
            if(this.myOrganisationSettings.fulfilmentOptions == this.FulfilmentOptions.ALL) {
                return this.$router.push({name: 'DeliveryMethod'})
            }

            if(this.issueInPersonOverride) {
                await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.IN_PERSON)
                await this.setShippingAddressTypeInTestStoreAction(0)
                return this.$router.push({name: 'TestDetails'})
            }

            await this.setDeliveryMethodInTestStoreAction(this.IssueMethod.POST)
            await this.setShippingAddressTypeInTestStoreAction(0)
            return this.$router.push({name: 'TestDetails'})
        },
    }
}
</script>